import React, { useCallback, useEffect, useState } from 'react'
import { Rate, Table, Typography, Button, Progress, message } from 'antd'
import { ReloadOutlined } from '@ant-design/icons'
import dayjs from 'dayjs'
import api from '../../app/api'
import { AdminLayout } from '../../components/AdminLayout'
import { useTranslation } from '../../shared/hooks/useTranslation'
import style from './BookingRatings.module.scss'

const { Title, Text } = Typography

interface FeedbackItem {
  id: number
  rating: number
  comment: string | null
  doctor_id: string | null
  service_name: string | null
  iin: string | null
  created_at: string
}

interface Summary {
  total: number
  average: number
  distribution: Record<string, number>
}

const EMPTY_SUMMARY: Summary = { total: 0, average: 0, distribution: {} }

export function BookingRatings() {
  const { t } = useTranslation()
  const [items, setItems] = useState<FeedbackItem[]>([])
  const [summary, setSummary] = useState<Summary>(EMPTY_SUMMARY)
  const [loading, setLoading] = useState(false)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const res = await api.get('/admin/feedback', {
        headers: { Authorization: `Bearer ${localStorage.getItem('adminToken')}` },
      })
      setItems(res.data?.items || [])
      setSummary(res.data?.summary || EMPTY_SUMMARY)
    } catch {
      message.error(t.adminRatingsLoadError)
    } finally {
      setLoading(false)
    }
  }, [t.adminRatingsLoadError])

  useEffect(() => {
    load()
  }, [load])

  return (
    <AdminLayout>
      <div className={style.content}>
        <div className={style.header}>
          <div>
            <Title level={4} style={{ marginBottom: 4, fontSize: 22, fontWeight: 600 }}>
              {t.adminRatingsTitle}
            </Title>
            <Text type="secondary">{t.adminRatingsSubtitle}</Text>
          </div>
          <Button icon={<ReloadOutlined />} onClick={load} loading={loading}>
            {t.adminRatingsRefresh}
          </Button>
        </div>

        <div className={style.summary}>
          <div className={style.summaryCard}>
            <span className={style.summaryLabel}>{t.adminRatingsAverage}</span>
            <span className={style.summaryValue}>
              {summary.average ? summary.average.toFixed(2) : '—'}
            </span>
            <Rate disabled allowHalf value={summary.average} />
          </div>

          <div className={style.summaryCard}>
            <span className={style.summaryLabel}>{t.adminRatingsTotal}</span>
            <span className={style.summaryValue}>{summary.total}</span>
          </div>

          <div className={style.distribution}>
            {[5, 4, 3, 2, 1].map((star) => {
              const count = summary.distribution?.[String(star)] || 0
              const percent = summary.total ? Math.round((count / summary.total) * 100) : 0
              return (
                <div key={star} className={style.distRow}>
                  <span className={style.distStar}>{star} ★</span>
                  <Progress percent={percent} showInfo={false} strokeColor="#f5a623" />
                  <span className={style.distCount}>{count}</span>
                </div>
              )
            })}
          </div>
        </div>

        <Table<FeedbackItem>
          rowKey="id"
          dataSource={items}
          loading={loading}
          pagination={{ pageSize: 20, showSizeChanger: false }}
          className={style.table}
          locale={{ emptyText: t.adminRatingsEmpty }}
          columns={[
            {
              title: t.adminRatingsColDate,
              dataIndex: 'created_at',
              width: 160,
              render: (v: string) => dayjs(v).format('DD.MM.YYYY HH:mm'),
            },
            {
              title: t.adminRatingsColRating,
              dataIndex: 'rating',
              width: 170,
              render: (v: number) => <Rate disabled value={v} />,
            },
            {
              title: t.adminRatingsColDoctor,
              dataIndex: 'doctor_id',
              width: 120,
              render: (v: string | null) => v || '—',
            },
            {
              title: t.adminRatingsColService,
              dataIndex: 'service_name',
              ellipsis: true,
              render: (v: string | null) => v || '—',
            },
            {
              title: t.adminRatingsColIin,
              dataIndex: 'iin',
              width: 140,
              render: (v: string | null) => v || '—',
            },
          ]}
        />
      </div>
    </AdminLayout>
  )
}
